/**
 * NEUGREEN Future Manufacturing Vision — concept-stage section for /neunexus.
 *
 * STRICT COMPLIANCE:
 *   - No internal codenames (NEUDI/NEUMI/NEUVI/NEUMANEU/NEUCORE/NEUMESH/
 *     NEUSIM/NEUCONTROL/NEUARENA) visible anywhere in the public UI.
 *   - No partner logos, no partnership claim.
 *   - Positioned as future concept only. No proprietary robot, no factory,
 *     no training facility already exists.
 *   - NeuNexus = integrator. NEUGREEN = clean-energy product use case.
 *
 * Additive only. Does NOT modify any existing section.
 * DE / EN / PT via the existing LangCtx.
 */
import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronRight, ArrowUpRight, ShieldCheck, Cpu, Bot, User2,
} from "lucide-react";
import { LangCtx } from "@/i18n/neunexus";

const P = {
  bg:      "#080B12",
  bg2:     "#0A0F1A",
  border:  "rgba(255,255,255,0.08)",
  surface: "rgba(255,255,255,0.03)",
  ink:     "#E8ECF0",
  dim:     "rgba(232,236,240,0.68)",
  silver:  "#C0C7CE",
  cyan:    "#06B6D4",
  green:   "#22C55E",
  amber:   "#F59E0B",
};

/* ------------------------------------------------------------------ */
/* Nine functional zones with normalized hotspot positions (%).        */
/* Coordinates match the public derivative image                       */
/* /innovation/neugreen-future-manufacturing.webp                      */
/* (image is 1672x941 → hotspots as percentage of that space).         */
/* ------------------------------------------------------------------ */
const ZONE_KEYS = [
  "assembly", "flow", "vision", "collab",
  "core", "mesh", "sim", "supervision", "pilot",
];

const HOTSPOTS = {
  assembly:    { x: 25.1, y: 11.1 },   // top-left chip
  flow:        { x: 63.5, y: 11.1 },   // top-center-right chip
  vision:      { x: 17.5, y: 42.1 },   // middle-left chip
  collab:      { x: 76.0, y: 42.1 },   // middle-right chip
  core:        { x: 48.5, y: 43.0 },   // center cylinder chip
  mesh:        { x: 48.5, y: 55.8 },   // center below cylinder
  sim:         { x: 28.4, y: 73.0 },   // bottom-left chip
  supervision: { x: 75.6, y: 73.0 },   // bottom-right chip
  pilot:       { x: 88.0, y:  8.0 },   // top-right wall label
};

const D = {
  en: {
    navItem: "NGX Manufacturing",
    status: "Concept Stage · Future Industrial Application",
    title: "NEUGREEN NGX Manufacturing System",
    tagline: "A future modular framework for intelligent energy-product manufacturing.",
    intro:
      "NEUGREEN is developing a phased manufacturing vision for future clean-energy products. The proposed model combines OEM knowledge transfer, robot-assisted assembly, mobile material flow, vision inspection, digital traceability and human-controlled quality.",
    central:
      "We are not building a new robot company. We are building a future intelligent manufacturing model for NEUGREEN energy products, using technology from qualified partners.",
    ctaExplore: "Explore the Manufacturing Model",
    ctaPilot:   "Discuss a Future Pilot",
    imageAlt:
      "Concept simulation of the future NEUGREEN NGX Manufacturing System, showing nine functional modules for assembly, material flow, vision inspection, human-robot collaboration, digital integration, data network, simulation, human supervision and a future pilot lab.",
    imgLabel: "Concept Simulation",
    zonesTitle: "NGX Modules · Interactive Overview",
    zonesHint:  "Point over a module on the image or open a card to read its role and the human responsibility.",
    moduleLabel: "Concept Module",
    zones: {
      assembly: {
        title: "NGX Assembly",
        meta:  "Robot-Assisted Assembly",
        short: "Controlled component handling, positioning and mechanical fastening.",
        long:  "Robot support for controlled component handling, positioning and mechanical fastening.",
        human: "Electrical connections, exception handling and final approval remain human responsibilities.",
      },
      flow: {
        title: "NGX Flow",
        meta:  "Transport & Material Flow",
        short: "Autonomous transport of approved components and material trays between work areas.",
        long:  "Future autonomous transport of approved components, material trays and production supplies between work areas.",
        human: "People define routes, loading rules, access permissions and safety limits.",
      },
      vision: {
        title: "NGX Vision",
        meta:  "Visual Quality Inspection",
        short: "Camera and sensor checks for component presence, orientation and quality.",
        long:  "Camera and sensor checks for component presence, orientation, position, serial identity and visible quality conditions.",
        human: "Qualified people define acceptance criteria and approve exceptions.",
      },
      collab: {
        title: "NGX Collaborate",
        meta:  "Human–Robot Collaboration",
        short: "Robots support repetitive physical tasks while qualified technicians supervise.",
        long:  "A future working model in which robots support repetitive physical tasks while qualified technicians supervise the process.",
        human: "People retain safety authority, technical judgment and final product approval.",
      },
      core: {
        title: "NGX Core",
        meta:  "Digital Integration",
        short: "Coordinates work orders, permissions, production status and quality information.",
        long:  "A proposed digital layer coordinating work orders, permissions, production status and quality information.",
        human: "Status — Conceptual software architecture.",
      },
      mesh: {
        title: "NGX Network",
        meta:  "Data & Traceability",
        short: "Connects equipment, inspection results, serial numbers and approved applications.",
        long:  "A proposed data structure connecting equipment, inspection results, serial numbers, production records and approved applications.",
        human: "Status — Conceptual platform architecture.",
      },
      sim: {
        title: "NGX Sim",
        meta:  "Simulation & Validation",
        short: "Prepare workflows and evaluate robotic tasks before physical implementation.",
        long:  "A future environment for preparing workflows, testing configurations and evaluating robotic tasks before physical implementation.",
        human: "Status — Conceptual simulation environment.",
      },
      supervision: {
        title: "NGX Control",
        meta:  "Human Supervision",
        short: "Monitor operations, stop processes, manage exceptions and approve results.",
        long:  "A proposed control interface for monitoring operations, stopping processes, managing exceptions and approving results.",
        human: "Human authority must remain visible at every critical point.",
      },
      pilot: {
        title: "NGX Pilot Lab",
        meta:  "Future Pilot & Training Area",
        short: "A future physical space for training, testing and validating industrial tasks.",
        long:  "A future physical space for training, testing and validating industrial tasks under controlled conditions.",
        human: "Future concept. No existing facility or operational deployment is implied.",
      },
    },
    respTitle: "Human and Robot Responsibilities",
    robotTitle: "Robot",
    robot: [
      "Handles approved non-energized components",
      "Supports controlled positioning",
      "Performs repeatable mechanical work",
      "Collects sensor and inspection data",
      "Stops when an exception is detected",
    ],
    humanTitle: "Human",
    human: [
      "Defines the task and working rules",
      "Performs electrical connections",
      "Handles unexpected situations",
      "Maintains safety authority",
      "Provides final quality approval",
    ],
    highlight:
      "The robot provides strength, precision and repeatability. The human provides knowledge, judgment and final authority.",
    roadmapTitle: "Development Roadmap",
    roadmap: [
      { n: "01", k: "OEM Product Selection",        v: "Select the battery cabinet, components, documentation and required certifications." },
      { n: "02", k: "Assembly Knowledge",            v: "Learn from the manufacturer how to assemble, test, maintain and service the product." },
      { n: "03", k: "Preparation for Automation",    v: "Identify which non-energized, repetitive tasks could be performed safely with robotic support." },
      { n: "04", k: "First Pilot Cell",              v: "Automate one simple and controlled task with qualified human supervision." },
      { n: "05", k: "Quality and Traceability",      v: "Integrate cameras, serial identification, production records, inspection results and human approval." },
      { n: "06", k: "Industrial Expansion",          v: "Evaluate future SKD/CKD assembly and additional NEUGREEN energy-product lines." },
    ],
    statusTitle: "Project Status",
    statuses: [
      { k: "Current", v: "OEM product evaluation, supplier discussions and manufacturing concept development." },
      { k: "Next",    v: "Select one product, obtain technical documentation and define one realistic pilot task." },
      { k: "Future",  v: "Pilot cell, trained team, digital traceability and phased industrial expansion." },
    ],
    positioningTitle: "Public Positioning",
    positioning:
      "NeuNexus coordinates the future integration model. NEUGREEN defines the clean-energy product and manufacturing use case. Robotic hardware, engineering services and industrial technologies would be provided by qualified partners.",
    ctaCardTitle: "From OEM Product to Intelligent Manufacturing",
    ctaCardBody:
      "The first objective is not a complete automated factory. It is one safe, measurable and realistic pilot task based on an approved NEUGREEN product.",
    disclaimer:
      "The NGX Manufacturing System is a future integration concept. It does not represent proprietary robots, an operational factory or commercially available products. Robotic hardware and industrial technologies would be provided by qualified partners.",
  },
  de: {
    navItem: "Zukunft der Fertigung",
    status: "Konzeptphase · Zukünftige industrielle Anwendung",
    title: "NEUGREEN Fertigungsvision der Zukunft",
    intro:
      "NEUGREEN entwickelt eine gestufte Fertigungsvision für zukünftige Clean-Energy-Produkte. Das vorgeschlagene Modell verbindet OEM-Wissenstransfer, roboterunterstützte Montage, mobilen Materialfluss, Sichtinspektion, digitale Rückverfolgbarkeit und menschlich gesteuerte Qualität.",
    central:
      "Wir bauen kein neues Roboterunternehmen. Wir entwickeln ein zukünftiges intelligentes Fertigungsmodell für NEUGREEN-Energieprodukte, mit Technologie von qualifizierten Partnern.",
    ctaExplore: "Fertigungsmodell entdecken",
    ctaPilot:   "Zukünftigen Pilot besprechen",
    imageAlt:
      "Konzeptsimulation einer zukünftigen intelligenten Fertigungsumgebung für NEUGREEN Clean-Energy-Produkte mit neun funktionalen Zonen für Montage, Materialfluss, Sichtinspektion, Mensch-Roboter-Zusammenarbeit, digitale Integration, Datennetzwerk, Simulation, menschliche Überwachung und einen zukünftigen Pilot- und Trainingsbereich.",
    imgLabel: "Konzeptsimulation",
    zonesTitle: "Interaktive Funktionszonen",
    zonesHint:  "Zeigen Sie auf eine Zone im Bild oder öffnen Sie eine Karte, um Rolle und menschliche Verantwortung zu lesen.",
    zones: {
      assembly: {
        title: "Roboterunterstützte Montage",
        short: "Kontrollierte Komponenten-Handhabung, Positionierung und mechanische Befestigung.",
        long:  "Roboterunterstützung für kontrollierte Komponenten-Handhabung, Positionierung und mechanische Befestigung.",
        human: "Elektrische Verbindungen, Ausnahmebehandlung und Endfreigabe bleiben in menschlicher Verantwortung.",
      },
      flow: {
        title: "Mobiler Materialfluss",
        short: "Autonomer Transport freigegebener Komponenten und Materialträger zwischen Arbeitsbereichen.",
        long:  "Zukünftiger autonomer Transport freigegebener Komponenten, Materialträger und Produktionsversorgungen zwischen Arbeitsbereichen.",
        human: "Menschen legen Routen, Beladungsregeln, Zugangsrechte und Sicherheitsgrenzen fest.",
      },
      vision: {
        title: "Sichtinspektion (Vision Quality)",
        short: "Kamera- und Sensorprüfungen für Vorhandensein, Ausrichtung und Qualität von Komponenten.",
        long:  "Kamera- und Sensorprüfungen für Vorhandensein, Ausrichtung, Position, Serienidentität und sichtbare Qualitätsmerkmale von Komponenten.",
        human: "Qualifizierte Personen definieren Akzeptanzkriterien und genehmigen Ausnahmen.",
      },
      collab: {
        title: "Mensch-Roboter-Kollaboration",
        short: "Roboter unterstützen repetitive körperliche Aufgaben, qualifizierte Techniker überwachen den Prozess.",
        long:  "Ein zukünftiges Arbeitsmodell, in dem Roboter repetitive körperliche Aufgaben unterstützen, während qualifizierte Techniker den Prozess überwachen.",
        human: "Menschen behalten Sicherheitsautorität, technisches Urteil und finale Produktfreigabe.",
      },
      core: {
        title: "Digitale Integrationsschicht",
        short: "Koordiniert Aufträge, Berechtigungen, Produktionsstatus und Qualitätsinformationen.",
        long:  "Eine vorgeschlagene digitale Schicht zur Koordination von Aufträgen, Berechtigungen, Produktionsstatus und Qualitätsinformationen.",
        human: "Status — Konzeptionelle Software-Architektur.",
      },
      mesh: {
        title: "Produktions-Datennetz",
        short: "Verbindet Anlagen, Prüfergebnisse, Seriennummern und freigegebene Anwendungen.",
        long:  "Eine vorgeschlagene Datenstruktur, die Anlagen, Prüfergebnisse, Seriennummern, Produktionsprotokolle und freigegebene Anwendungen verbindet.",
        human: "Status — Konzeptionelle Plattform-Architektur.",
      },
      sim: {
        title: "Simulation und Validierung",
        short: "Arbeitsabläufe vorbereiten und robotische Aufgaben vor physischer Umsetzung bewerten.",
        long:  "Eine zukünftige Umgebung zur Vorbereitung von Arbeitsabläufen, zum Testen von Konfigurationen und zur Bewertung robotischer Aufgaben vor physischer Umsetzung.",
        human: "Status — Konzeptionelle Simulationsumgebung.",
      },
      supervision: {
        title: "Menschliche Überwachung",
        short: "Überwachen, Stoppen, Ausnahmebehandlung und Ergebnisfreigabe.",
        long:  "Eine vorgeschlagene Kontrolloberfläche zur Überwachung, zum Stoppen von Prozessen, zur Ausnahmebehandlung und zur Ergebnisfreigabe.",
        human: "Menschliche Autorität muss an jedem kritischen Punkt sichtbar bleiben.",
      },
      pilot: {
        title: "Zukünftiger Pilot- und Trainingsbereich",
        short: "Ein zukünftiger physischer Raum zum Trainieren, Testen und Validieren industrieller Aufgaben.",
        long:  "Ein zukünftiger physischer Raum zum Trainieren, Testen und Validieren industrieller Aufgaben unter kontrollierten Bedingungen.",
        human: "Zukünftiges Konzept. Keine bestehende Einrichtung oder operative Umsetzung wird impliziert.",
      },
    },
    respTitle: "Verantwortlichkeiten von Mensch und Roboter",
    robotTitle: "Roboter",
    robot: [
      "Handhabt zugelassene, spannungsfreie Komponenten",
      "Unterstützt kontrollierte Positionierung",
      "Führt wiederholbare mechanische Arbeiten aus",
      "Erfasst Sensor- und Inspektionsdaten",
      "Hält an, sobald eine Ausnahme erkannt wird",
    ],
    humanTitle: "Mensch",
    human: [
      "Definiert Aufgabe und Arbeitsregeln",
      "Führt elektrische Verbindungen aus",
      "Bewältigt unerwartete Situationen",
      "Behält die Sicherheitsautorität",
      "Erteilt die finale Qualitätsfreigabe",
    ],
    highlight:
      "Der Roboter liefert Kraft, Präzision und Wiederholbarkeit. Der Mensch liefert Wissen, Urteil und finale Autorität.",
    roadmapTitle: "Entwicklungsfahrplan",
    roadmap: [
      { n: "01", k: "OEM-Produktauswahl",             v: "Auswahl des Batterieschranks, der Komponenten, der Dokumentation und der erforderlichen Zertifizierungen." },
      { n: "02", k: "Montage-Wissen",                  v: "Vom Hersteller lernen, wie das Produkt montiert, geprüft, gewartet und serviciert wird." },
      { n: "03", k: "Vorbereitung der Automatisierung",v: "Identifizieren, welche spannungsfreien, wiederholbaren Aufgaben mit Roboterunterstützung sicher ausgeführt werden könnten." },
      { n: "04", k: "Erste Pilotzelle",                v: "Automatisierung einer einfachen und kontrollierten Aufgabe unter qualifizierter menschlicher Überwachung." },
      { n: "05", k: "Qualität und Rückverfolgbarkeit", v: "Integration von Kameras, Serienidentifikation, Produktionsprotokollen, Prüfergebnissen und menschlicher Freigabe." },
      { n: "06", k: "Industrielle Skalierung",         v: "Bewertung zukünftiger SKD/CKD-Montage und weiterer NEUGREEN-Energieproduktlinien." },
    ],
    statusTitle: "Projektstatus",
    statuses: [
      { k: "Aktuell",   v: "OEM-Produktevaluation, Lieferantengespräche und Entwicklung des Fertigungskonzepts." },
      { k: "Nächstes",  v: "Ein Produkt auswählen, technische Dokumentation beschaffen und eine realistische Pilotaufgabe definieren." },
      { k: "Zukunft",   v: "Pilotzelle, geschultes Team, digitale Rückverfolgbarkeit und stufenweise industrielle Skalierung." },
    ],
    positioningTitle: "Öffentliche Positionierung",
    positioning:
      "NeuNexus koordiniert das zukünftige Integrationsmodell. NEUGREEN definiert das Clean-Energy-Produkt und den Fertigungs-Anwendungsfall. Roboter-Hardware, Engineering-Leistungen und industrielle Technologien würden von qualifizierten Partnern bereitgestellt.",
    ctaCardTitle: "Vom OEM-Produkt zur intelligenten Fertigung",
    ctaCardBody:
      "Das erste Ziel ist keine komplette automatisierte Fabrik. Es ist eine sichere, messbare und realistische Pilotaufgabe auf Basis eines freigegebenen NEUGREEN-Produkts.",
    disclaimer:
      "Nur Konzeptsimulation. Das Fertigungsmodell, die Roboterkonfiguration, die Sicherheitsarchitektur, der Trainingsprozess und der Umsetzungspfad sind noch nicht finalisiert. Es wird keine bestehende Fabrik, kein proprietärer Roboter, keine technische Zulassung, kein kommerzieller Einsatz und keine bestätigte Technologiepartnerschaft impliziert.",
  },
  pt: {
    navItem: "Fabricação Futura",
    status: "Fase de Conceito · Aplicação Industrial Futura",
    title: "Visão de Fabricação Futura NEUGREEN",
    intro:
      "A NEUGREEN está a desenvolver uma visão faseada de fabricação para futuros produtos de energia limpa. O modelo proposto combina transferência de conhecimento OEM, montagem assistida por robôs, fluxo móvel de materiais, inspeção por visão, rastreabilidade digital e qualidade sob controlo humano.",
    central:
      "Não estamos a criar uma nova empresa de robôs. Estamos a construir um futuro modelo de fabricação inteligente para os produtos energéticos NEUGREEN, com tecnologia de parceiros qualificados.",
    ctaExplore: "Explorar o modelo de fabricação",
    ctaPilot:   "Discutir um piloto futuro",
    imageAlt:
      "Simulação conceptual de um futuro ambiente de fabricação inteligente para produtos NEUGREEN de energia limpa, mostrando nove zonas funcionais para montagem, fluxo de materiais, inspeção por visão, colaboração humano-robô, integração digital, rede de dados, simulação, supervisão humana e uma futura área de piloto e treino.",
    imgLabel: "Simulação Conceptual",
    zonesTitle: "Zonas Funcionais Interativas",
    zonesHint:  "Aponte para uma zona na imagem ou abra um cartão para ler o papel e a responsabilidade humana.",
    zones: {
      assembly: {
        title: "Montagem Assistida por Robô",
        short: "Manuseamento, posicionamento e fixação mecânica controlada de componentes.",
        long:  "Suporte robótico para manuseamento, posicionamento e fixação mecânica controlada de componentes.",
        human: "As ligações elétricas, o tratamento de exceções e a aprovação final continuam a ser responsabilidades humanas.",
      },
      flow: {
        title: "Fluxo Móvel de Materiais",
        short: "Transporte autónomo de componentes aprovados e tabuleiros de material entre áreas de trabalho.",
        long:  "Futuro transporte autónomo de componentes aprovados, tabuleiros de material e abastecimento de produção entre áreas de trabalho.",
        human: "As pessoas definem rotas, regras de carga, permissões de acesso e limites de segurança.",
      },
      vision: {
        title: "Inspeção Visual de Qualidade",
        short: "Verificações por câmara e sensor para presença, orientação e qualidade dos componentes.",
        long:  "Verificações por câmara e sensor para presença, orientação, posição, identidade de série e condições visuais de qualidade dos componentes.",
        human: "Pessoas qualificadas definem critérios de aceitação e aprovam exceções.",
      },
      collab: {
        title: "Colaboração Humano-Robô",
        short: "Robôs apoiam tarefas físicas repetitivas enquanto técnicos qualificados supervisionam.",
        long:  "Um modelo de trabalho futuro em que os robôs apoiam tarefas físicas repetitivas enquanto técnicos qualificados supervisionam o processo.",
        human: "As pessoas mantêm a autoridade de segurança, o juízo técnico e a aprovação final do produto.",
      },
      core: {
        title: "Núcleo de Integração Digital",
        short: "Coordena ordens de trabalho, permissões, estado de produção e informação de qualidade.",
        long:  "Uma camada digital proposta que coordena ordens de trabalho, permissões, estado de produção e informação de qualidade.",
        human: "Estado — Arquitetura de software conceptual.",
      },
      mesh: {
        title: "Rede de Dados de Produção",
        short: "Liga equipamentos, resultados de inspeção, números de série e aplicações aprovadas.",
        long:  "Uma estrutura de dados proposta que liga equipamentos, resultados de inspeção, números de série, registos de produção e aplicações aprovadas.",
        human: "Estado — Arquitetura de plataforma conceptual.",
      },
      sim: {
        title: "Simulação e Validação",
        short: "Preparar fluxos de trabalho e avaliar tarefas robóticas antes da execução física.",
        long:  "Um ambiente futuro para preparar fluxos de trabalho, testar configurações e avaliar tarefas robóticas antes da execução física.",
        human: "Estado — Ambiente de simulação conceptual.",
      },
      supervision: {
        title: "Supervisão Humana",
        short: "Monitorizar operações, parar processos, gerir exceções e aprovar resultados.",
        long:  "Uma interface de controlo proposta para monitorizar operações, parar processos, gerir exceções e aprovar resultados.",
        human: "A autoridade humana deve permanecer visível em cada ponto crítico.",
      },
      pilot: {
        title: "Área Futura de Piloto e Treino",
        short: "Um espaço físico futuro para treino, teste e validação de tarefas industriais.",
        long:  "Um espaço físico futuro para treino, teste e validação de tarefas industriais em condições controladas.",
        human: "Conceito futuro. Não se implica qualquer instalação existente nem implementação operacional.",
      },
    },
    respTitle: "Responsabilidades Humano e Robô",
    robotTitle: "Robô",
    robot: [
      "Manuseia componentes aprovados e não energizados",
      "Apoia o posicionamento controlado",
      "Executa trabalho mecânico repetível",
      "Recolhe dados de sensores e inspeção",
      "Para quando é detetada uma exceção",
    ],
    humanTitle: "Humano",
    human: [
      "Define a tarefa e as regras de trabalho",
      "Executa ligações elétricas",
      "Trata de situações inesperadas",
      "Mantém a autoridade de segurança",
      "Aprova a qualidade final",
    ],
    highlight:
      "O robô fornece força, precisão e repetibilidade. O humano fornece conhecimento, juízo e autoridade final.",
    roadmapTitle: "Roteiro de Desenvolvimento",
    roadmap: [
      { n: "01", k: "Seleção de produto OEM",           v: "Selecionar o armário de baterias, componentes, documentação e certificações necessárias." },
      { n: "02", k: "Conhecimento de montagem",          v: "Aprender com o fabricante como montar, testar, manter e assistir o produto." },
      { n: "03", k: "Preparação para automação",         v: "Identificar quais tarefas repetitivas e não energizadas podem ser executadas com segurança com apoio robótico." },
      { n: "04", k: "Primeira célula piloto",            v: "Automatizar uma tarefa simples e controlada com supervisão humana qualificada." },
      { n: "05", k: "Qualidade e rastreabilidade",       v: "Integrar câmaras, identificação de série, registos de produção, resultados de inspeção e aprovação humana." },
      { n: "06", k: "Expansão industrial",               v: "Avaliar futura montagem SKD/CKD e linhas adicionais de produtos energéticos NEUGREEN." },
    ],
    statusTitle: "Estado do Projeto",
    statuses: [
      { k: "Atual",     v: "Avaliação de produto OEM, conversas com fornecedores e desenvolvimento do conceito de fabricação." },
      { k: "Seguinte",  v: "Selecionar um produto, obter documentação técnica e definir uma tarefa piloto realista." },
      { k: "Futuro",    v: "Célula piloto, equipa formada, rastreabilidade digital e expansão industrial faseada." },
    ],
    positioningTitle: "Posicionamento Público",
    positioning:
      "A NeuNexus coordena o modelo futuro de integração. A NEUGREEN define o produto de energia limpa e o caso de uso de fabricação. Hardware robótico, serviços de engenharia e tecnologias industriais seriam fornecidos por parceiros qualificados.",
    ctaCardTitle: "Do Produto OEM à Fabricação Inteligente",
    ctaCardBody:
      "O primeiro objetivo não é uma fábrica automatizada completa. É uma tarefa piloto segura, mensurável e realista baseada num produto NEUGREEN aprovado.",
    disclaimer:
      "Apenas simulação de conceito. O modelo de fabricação, a configuração robótica, a arquitetura de segurança, o processo de formação e o percurso de implementação ainda não foram finalizados. Não se implica qualquer fábrica existente, robô proprietário, aprovação técnica, implementação comercial ou parceria tecnológica confirmada.",
  },
};

const scrollToContact = (e) => {
  e.preventDefault();
  const el = document.getElementById("contact");
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const scrollToVisual = (e) => {
  e.preventDefault();
  const el = document.getElementById("future-manufacturing-visual");
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

/* Small helper — plaque row for a functional zone card */
function ZoneCard({ zoneKey, data, open, onToggle }) {
  const label = data.zones[zoneKey];
  return (
    <div
      data-testid={`fm-card-${zoneKey}`}
      id={`fm-zone-${zoneKey}`}
      className="rounded-lg overflow-hidden"
      style={{ background: P.surface, border: `1px solid ${open ? `${P.cyan}66` : P.border}` }}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={`fm-panel-${zoneKey}`}
        data-testid={`fm-card-button-${zoneKey}`}
        className="w-full flex items-start gap-3 p-4 text-left transition hover:brightness-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400"
      >
        <div
          className="shrink-0 w-9 h-9 rounded-md flex items-center justify-center font-mono text-[11px] font-semibold"
          style={{ background: `${P.cyan}18`, border: `1px solid ${P.cyan}55`, color: P.cyan }}
        >
          {String(ZONE_KEYS.indexOf(zoneKey) + 1).padStart(2, "0")}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[13.5px] font-semibold" style={{ color: "#fff" }}>{label.title}</div>
          <div className="mt-0.5 text-[12px] leading-relaxed" style={{ color: P.dim }}>{label.short}</div>
        </div>
        <ChevronRight size={16} className={`shrink-0 transition-transform ${open ? "rotate-90" : ""}`} style={{ color: P.silver }} />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            id={`fm-panel-${zoneKey}`}
            key="panel"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 pt-1 grid gap-2 text-[12.5px] leading-relaxed" style={{ color: P.ink, opacity: 0.9 }}>
              <p>{label.long}</p>
              <div
                className="rounded-md p-3 mt-1"
                style={{ background: `${P.green}10`, border: `1px solid ${P.green}33` }}
              >
                <div className="text-[10px] uppercase tracking-[0.24em] font-semibold" style={{ color: P.green }}>
                  {label.human.startsWith("Status") || label.human.startsWith("Estado") ? "Status" : "Human responsibility"}
                </div>
                <div className="mt-1" style={{ color: P.ink, opacity: 0.9 }}>{label.human}</div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function NeuGreenFutureManufacturing() {
  const { lang } = React.useContext(LangCtx);
  const t = D[lang] || D.en;

  const [openKey, setOpenKey] = React.useState(null);
  const [hoverKey, setHoverKey] = React.useState(null);

  // On mount + on hash change, open the matching zone
  React.useEffect(() => {
    const handle = () => {
      const h = window.location.hash.replace(/^#/, "");
      const map = {
        "robot-assisted-assembly": "assembly",
        "mobile-material-flow": "flow",
        "vision-quality-inspection": "vision",
        "human-robot-collaboration": "collab",
        "digital-integration-core": "core",
        "production-data-network": "mesh",
        "simulation-and-validation": "sim",
        "human-supervision": "supervision",
        "future-pilot-and-training-area": "pilot",
      };
      if (map[h]) {
        setOpenKey(map[h]);
        const card = document.getElementById(`fm-zone-${map[h]}`);
        if (card) card.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    };
    handle();
    window.addEventListener("hashchange", handle);
    return () => window.removeEventListener("hashchange", handle);
  }, []);

  const setOpenAndHash = (key) => {
    const next = openKey === key ? null : key;
    setOpenKey(next);
    if (next) {
      const anchors = {
        assembly: "robot-assisted-assembly",
        flow: "mobile-material-flow",
        vision: "vision-quality-inspection",
        collab: "human-robot-collaboration",
        core: "digital-integration-core",
        mesh: "production-data-network",
        sim: "simulation-and-validation",
        supervision: "human-supervision",
        pilot: "future-pilot-and-training-area",
      };
      history.replaceState(null, "", `#${anchors[next]}`);
    }
  };

  return (
    <section
      id="future-manufacturing"
      data-testid="future-manufacturing-section"
      className="relative py-24 px-6 lg:px-12 border-t"
      style={{
        background: `linear-gradient(180deg, ${P.bg} 0%, ${P.bg2} 100%)`,
        borderColor: P.border,
      }}
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none opacity-70"
        style={{
          backgroundImage: `radial-gradient(circle at 12% 8%, ${P.cyan}0f 0%, transparent 55%), radial-gradient(circle at 88% 92%, ${P.green}0f 0%, transparent 55%)`,
        }}
      />

      <div className="relative max-w-6xl mx-auto">
        {/* Status pill + title + intro */}
        <motion.div
          initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.6 }}
        >
          <div
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10.5px] uppercase tracking-[0.28em] font-semibold"
            style={{ background: `${P.cyan}12`, border: `1px solid ${P.cyan}55`, color: P.cyan }}
            data-testid="fm-status-pill"
          >
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: P.cyan }} />
            {t.status}
          </div>
          <h2
            className="mt-5 font-semibold tracking-tight text-[26px] sm:text-[34px] lg:text-[44px] leading-[1.05]"
            style={{ color: "#fff", letterSpacing: "-0.02em" }}
          >
            {t.title}
          </h2>
          <p className="mt-5 max-w-3xl text-[14px] leading-relaxed" style={{ color: P.dim }}>
            {t.intro}
          </p>
          <div
            className="mt-6 max-w-3xl rounded-xl p-5"
            style={{
              background: `linear-gradient(135deg, ${P.green}0d 0%, ${P.bg2} 100%)`,
              border: `1px solid ${P.green}44`,
              color: P.ink,
            }}
            data-testid="fm-central-message"
          >
            <div className="flex gap-3 items-start">
              <ShieldCheck size={18} style={{ color: P.green }} className="shrink-0 mt-0.5" />
              <p className="text-[13px] leading-relaxed" style={{ color: P.ink, opacity: 0.95 }}>
                {t.central}
              </p>
            </div>
          </div>
          {/* Primary + secondary CTA */}
          <div className="mt-6 flex flex-wrap gap-3">
            <a
              href="#future-manufacturing-visual"
              onClick={scrollToVisual}
              data-testid="fm-cta-explore"
              className="inline-flex items-center gap-2 px-5 py-3 text-[12.5px] font-semibold rounded-md transition hover:brightness-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400"
              style={{ background: P.cyan, color: "#04121a" }}
            >
              {t.ctaExplore} <ArrowUpRight size={13} />
            </a>
            <a
              href="#contact"
              onClick={scrollToContact}
              data-testid="fm-cta-pilot"
              className="inline-flex items-center gap-2 px-5 py-3 text-[12.5px] font-semibold rounded-md transition hover:brightness-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400"
              style={{ background: `${P.green}18`, color: P.green, border: `1px solid ${P.green}55` }}
            >
              {t.ctaPilot} <ChevronRight size={13} />
            </a>
          </div>
        </motion.div>

        {/* Interactive visual */}
        <motion.figure
          id="future-manufacturing-visual"
          initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.6 }}
          className="relative mt-10 rounded-2xl overflow-hidden"
          style={{
            background: P.bg2,
            border: `1px solid ${P.border}`,
            boxShadow: `0 0 0 1px ${P.cyan}22, 0 30px 80px -30px ${P.cyan}55`,
          }}
          data-testid="fm-visual"
        >
          <img
            src="/innovation/neugreen-future-manufacturing.webp"
            alt={t.imageAlt}
            loading="lazy"
            decoding="async"
            width="1600" height="900"
            className="w-full h-auto object-contain block"
            data-testid="fm-image"
          />

          {/* Hotspots — desktop only (lg+) */}
          <div className="hidden lg:block absolute inset-0" aria-hidden={false}>
            {ZONE_KEYS.map((k) => {
              const pos = HOTSPOTS[k];
              const label = t.zones[k];
              const active = hoverKey === k || openKey === k;
              return (
                <button
                  key={k}
                  type="button"
                  data-testid={`fm-hotspot-${k}`}
                  aria-label={label.title}
                  onMouseEnter={() => setHoverKey(k)}
                  onMouseLeave={() => setHoverKey(null)}
                  onFocus={() => setHoverKey(k)}
                  onBlur={() => setHoverKey(null)}
                  onClick={() => setOpenAndHash(k)}
                  className="absolute rounded-full transition focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400"
                  style={{
                    left: `calc(${pos.x}% - 12px)`,
                    top: `calc(${pos.y}% - 12px)`,
                    width: 24,
                    height: 24,
                    background: active ? `${P.cyan}` : "rgba(6,182,212,0.30)",
                    border: `2px solid ${active ? P.cyan : "rgba(6,182,212,0.75)"}`,
                    boxShadow: active
                      ? `0 0 0 6px ${P.cyan}22, 0 0 24px ${P.cyan}88`
                      : `0 0 0 4px rgba(6,182,212,0.14)`,
                  }}
                />
              );
            })}

            {/* Hover tooltip */}
            {hoverKey && (
              <div
                className="pointer-events-none absolute z-10 rounded-md p-3 max-w-xs"
                style={{
                  left: `min(calc(${HOTSPOTS[hoverKey].x}% + 22px), calc(100% - 260px))`,
                  top:  `max(calc(${HOTSPOTS[hoverKey].y}% - 8px), 12px)`,
                  background: "rgba(8,11,18,0.94)",
                  border: `1px solid ${P.cyan}55`,
                  color: P.ink,
                  backdropFilter: "blur(6px)",
                }}
                data-testid={`fm-tooltip-${hoverKey}`}
              >
                <div className="text-[11px] uppercase tracking-[0.22em] font-semibold" style={{ color: P.cyan }}>
                  {t.zones[hoverKey].title}
                </div>
                <div className="mt-1 text-[12px] leading-relaxed" style={{ color: P.dim }}>
                  {t.zones[hoverKey].short}
                </div>
              </div>
            )}
          </div>

          {/* Concept simulation badge — mirrors the label already burned into the image */}
          <figcaption
            className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[10px] uppercase tracking-[0.24em] font-semibold"
            style={{ background: "rgba(8,11,18,0.82)", border: `1px solid ${P.cyan}55`, color: P.cyan, backdropFilter: "blur(6px)" }}
          >
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: P.cyan }} />
            {t.imgLabel}
          </figcaption>
        </motion.figure>

        {/* Zone cards — always visible; expand on click */}
        <div className="mt-10">
          <div className="text-[10px] uppercase tracking-[0.32em] font-semibold" style={{ color: P.silver }}>
            {t.zonesTitle}
          </div>
          <p className="mt-2 text-[12.5px]" style={{ color: P.dim }}>{t.zonesHint}</p>
          <div className="mt-4 grid md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="fm-cards">
            {ZONE_KEYS.map((k) => (
              <ZoneCard
                key={k}
                zoneKey={k}
                data={t}
                open={openKey === k}
                onToggle={() => setOpenAndHash(k)}
              />
            ))}
          </div>
        </div>

        {/* Human + Robot responsibilities */}
        <div className="mt-14">
          <div className="text-[10px] uppercase tracking-[0.32em] font-semibold" style={{ color: P.silver }}>
            {t.respTitle}
          </div>
          <div className="mt-4 grid md:grid-cols-2 gap-4" data-testid="fm-responsibilities">
            <div
              className="rounded-lg p-5"
              style={{ background: P.surface, border: `1px solid ${P.border}`, borderTop: `2px solid ${P.cyan}66` }}
            >
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] font-semibold" style={{ color: P.cyan }}>
                <Bot size={14} /> {t.robotTitle}
              </div>
              <ul className="mt-3 space-y-2 text-[12.5px] leading-relaxed" style={{ color: P.ink, opacity: 0.9 }}>
                {t.robot.map((line, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="mt-1.5 w-1 h-1 rounded-full shrink-0" style={{ background: P.cyan }} />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div
              className="rounded-lg p-5"
              style={{ background: P.surface, border: `1px solid ${P.border}`, borderTop: `2px solid ${P.green}66` }}
            >
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] font-semibold" style={{ color: P.green }}>
                <User2 size={14} /> {t.humanTitle}
              </div>
              <ul className="mt-3 space-y-2 text-[12.5px] leading-relaxed" style={{ color: P.ink, opacity: 0.9 }}>
                {t.human.map((line, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="mt-1.5 w-1 h-1 rounded-full shrink-0" style={{ background: P.green }} />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div
            className="mt-4 rounded-xl p-5"
            style={{ background: `linear-gradient(135deg, ${P.cyan}12 0%, ${P.green}0d 100%)`, border: `1px solid ${P.cyan}55` }}
            data-testid="fm-highlight"
          >
            <p className="text-[13.5px] leading-relaxed" style={{ color: "#fff" }}>{t.highlight}</p>
          </div>
        </div>

        {/* Roadmap */}
        <div className="mt-14">
          <div className="text-[10px] uppercase tracking-[0.32em] font-semibold" style={{ color: P.silver }}>
            {t.roadmapTitle}
          </div>
          {/* Desktop: horizontal grid; Mobile: vertical timeline */}
          <div className="mt-4 grid gap-3 md:grid-cols-3 lg:grid-cols-6" data-testid="fm-roadmap">
            {t.roadmap.map((step) => (
              <div
                key={step.n}
                className="rounded-lg p-4"
                style={{ background: P.surface, border: `1px solid ${P.border}`, borderTop: `2px solid ${P.cyan}66` }}
              >
                <div className="text-[10px] uppercase tracking-[0.24em] font-semibold font-mono" style={{ color: P.cyan }}>
                  Phase {step.n}
                </div>
                <div className="mt-2 text-[13px] font-semibold" style={{ color: "#fff" }}>{step.k}</div>
                <div className="mt-1.5 text-[12px] leading-relaxed" style={{ color: P.dim }}>{step.v}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Project Status panel */}
        <div className="mt-14">
          <div className="text-[10px] uppercase tracking-[0.32em] font-semibold" style={{ color: P.silver }}>
            {t.statusTitle}
          </div>
          <div className="mt-4 grid sm:grid-cols-3 gap-3" data-testid="fm-status-panel">
            {t.statuses.map((s, i) => {
              const color = [P.cyan, P.amber, P.green][i];
              return (
                <div
                  key={i}
                  className="rounded-lg p-5"
                  style={{ background: P.surface, border: `1px solid ${P.border}`, borderTop: `2px solid ${color}66` }}
                >
                  <div className="text-[10px] uppercase tracking-[0.24em] font-semibold" style={{ color }}>{s.k}</div>
                  <div className="mt-2 text-[12.5px] leading-relaxed" style={{ color: P.ink, opacity: 0.9 }}>{s.v}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Public positioning */}
        <div
          className="mt-14 rounded-xl p-6"
          style={{ background: P.surface, border: `1px solid ${P.border}` }}
          data-testid="fm-positioning"
        >
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: P.silver }}>
            <Cpu size={14} /> {t.positioningTitle}
          </div>
          <p className="mt-3 text-[13px] leading-relaxed max-w-4xl" style={{ color: P.ink, opacity: 0.92 }}>
            {t.positioning}
          </p>
        </div>

        {/* CTA card */}
        <div
          className="mt-10 rounded-xl p-6 lg:p-8"
          style={{ background: `linear-gradient(135deg, ${P.green}0f 0%, ${P.bg2} 100%)`, border: `1px solid ${P.green}55` }}
          data-testid="fm-cta-card"
        >
          <h3 className="text-[20px] lg:text-[26px] font-semibold tracking-tight" style={{ color: "#fff", letterSpacing: "-0.02em" }}>
            {t.ctaCardTitle}
          </h3>
          <p className="mt-3 text-[13.5px] leading-relaxed max-w-3xl" style={{ color: P.ink, opacity: 0.9 }}>
            {t.ctaCardBody}
          </p>
          <div className="mt-5">
            <a
              href="#contact"
              onClick={scrollToContact}
              data-testid="fm-cta-pilot-2"
              className="inline-flex items-center gap-2 px-5 py-3 text-[12.5px] font-semibold rounded-md transition hover:brightness-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400"
              style={{ background: P.green, color: "#04121a" }}
            >
              {t.ctaPilot} <ChevronRight size={13} />
            </a>
          </div>
        </div>

        {/* Disclaimer */}
        <p
          className="mt-8 text-[11.5px] italic leading-relaxed p-4 rounded-md max-w-4xl"
          style={{ background: "rgba(255,255,255,0.02)", border: `1px solid ${P.border}`, color: P.dim }}
          data-testid="fm-disclaimer"
        >
          {t.disclaimer}
        </p>
      </div>
    </section>
  );
}

// Convenience export — nav item label helper for the parent page
export const useFutureManufacturingNav = () => {
  const { lang } = React.useContext(LangCtx);
  return (D[lang] || D.en).navItem;
};
